import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import axios from "axios";
import { X, Save, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function DriverChargesModal({
  open,
  onOpenChange,
  baseurl,
  token,
  setIsUnauthorised,
  adminId,
  drivers = [],
  initialDriverCode = "", // optional (when clicking Edit from card)
  onSuccess,
}) {
  const [driverCode, setDriverCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // store existing ids (if present) to decide update vs add
  const [existing, setExisting] = useState({
    per_day: { id: null, amount: "" },
    per_order: { id: null, amount: "" },
  });

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.driverId === driverCode) || null,
    [drivers, driverCode]
  );

  const reset = () => {
    setDriverCode("");
    setExisting({
      per_day: { id: null, amount: "" },
      per_order: { id: null, amount: "" },
    });
  };

  const hydrateFromApi = (payloadData) => {
    // payloadData: [{driverCode, charges:[{id,type,amount}]}]
    const group = (payloadData || [])[0];
    const charges = group?.charges || [];
    const perDay = charges.find((c) => c.type === "per_day") || null;
    const perOrder = charges.find((c) => c.type === "per_order") || null;

    setExisting({
      per_day: {
        id: perDay?.id ?? null,
        amount: perDay?.amount?.toString?.() ?? "",
      },
      per_order: {
        id: perOrder?.id ?? null,
        amount: perOrder?.amount?.toString?.() ?? "",
      },
    });
  };

  const fetchChargesForDriver = async (code) => {
    if (!code) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/getDriverCharges`,
        { driverCode: code },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        hydrateFromApi(res.data.data || []);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised?.(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to fetch charges");
      }
    } catch (e) {
      toast.error("Error fetching driver charges");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    // when opening modal via Edit card, preselect driver and fetch
    if (initialDriverCode) {
      setDriverCode(initialDriverCode);
      fetchChargesForDriver(initialDriverCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDriverCode]);

  const onDriverChange = (val) => {
    setDriverCode(val);
    setExisting({
      per_day: { id: null, amount: "" },
      per_order: { id: null, amount: "" },
    });
    if (val) fetchChargesForDriver(val);
  };

  const validateAmount = (v) => /^\d+(\.\d+)?$/.test(String(v || "").trim());

  const saveOne = async ({ type, id, amount }) => {
    if (!amount && amount !== 0) return; // skip empty

    if (!validateAmount(amount)) {
      throw new Error(`${type} amount must be numeric`);
    }

    if (id) {
      // update
      const payload = {
        adminId,
        id: String(id),
        type,
        amount: String(amount),
      };
      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/updateDriverCharges`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );
      return res;
    } else {
      // add
      const payload = {
        adminId,
        driverCode,
        type,
        amount: String(amount),
      };
      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/addDriverCharges`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );
      return res;
    }
  };

  const handleSave = async () => {
    if (!driverCode) return toast.error("Please select a driver.");

    setSaving(true);
    try {
      // save both (per_day + per_order). If one is empty, it will be skipped.
      const res1 = await saveOne({
        type: "per_day",
        id: existing.per_day.id,
        amount: existing.per_day.amount,
      });
      const res2 = await saveOne({
        type: "per_order",
        id: existing.per_order.id,
        amount: existing.per_order.amount,
      });

      const ok1 = !res1 || res1.data?.responseCode === "00";
      const ok2 = !res2 || res2.data?.responseCode === "00";

      if (ok1 && ok2) {
        toast.success("Driver charges saved");
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error("Some charges failed to save");
      }
    } catch (e) {
      toast.error(e?.message || "Error saving charges");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-xl bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-orange-700">
                <Wallet size={18} />
              </div>
              <div>
                <Dialog.Title className="text-base font-semibold text-gray-900">
                  Driver Charges
                </Dialog.Title>
                <p className="text-xs text-gray-600">
                  Set per-day & per-order charges
                </p>
              </div>
            </div>

            <Dialog.Close asChild>
              <button className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">
                Select Driver
              </label>

              <select
                className="w-full border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                value={driverCode}
                onChange={(e) => onDriverChange(e.target.value)}
              >
                <option value="">-- Select --</option>
                {drivers.map((d) => (
                  <option key={d.driverId} value={d.driverId}>
                    {d.driverId} • {d.fullName || d.username || "-"} • {d.teamName || "-"}
                  </option>
                ))}
              </select>

              {selectedDriver && (
                <div className="mt-2 text-xs text-gray-600">
                  <b>{selectedDriver.fullName || selectedDriver.username}</b> •{" "}
                  {selectedDriver.phone || "-"} • {selectedDriver.teamName || "-"}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ChargesCard
                title="Per Day"
                type="per_day"
                value={existing.per_day.amount}
                setValue={(v) =>
                  setExisting((p) => ({ ...p, per_day: { ...p.per_day, amount: v } }))
                }
                loading={loading}
              />
              <ChargesCard
                title="Per Order"
                type="per_order"
                value={existing.per_order.amount}
                setValue={(v) =>
                  setExisting((p) => ({
                    ...p,
                    per_order: { ...p.per_order, amount: v },
                  }))
                }
                loading={loading}
              />
            </div>

            <div className="rounded-lg border bg-gray-50 p-3 text-xs text-gray-600">
              {loading
                ? "Fetching existing charges..."
                : driverCode
                ? "If charges already exist, they are prefilled. Update and Save."
                : "Select a driver to begin."}
            </div>
          </div>

          <div className="px-5 py-4 border-t flex items-center justify-between">
            <button
              className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Close
            </button>

            <button
              className="inline-flex items-center gap-2 px-3 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white text-sm disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={16} />
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ChargesCard({ title, type, value, setValue, loading }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-gray-700">{title}</div>
        <span className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700">
          {type}
        </span>
      </div>

      <div className="mt-3">
        <label className="text-[11px] text-gray-500">Amount</label>
        <input
          className="mt-1 w-full border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter amount"
          disabled={loading}
          autoComplete="off"
        />
      </div>
    </div>
  );
}
